import { type LTLQuickQuoteResponseQuoteError } from 'lib/api/getLTLQuickQuotes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

export const TAI_LTL_QUOTE_ERROR_STOP_ROLE = {
  Pickup: 'pickup',
  Dropoff: 'dropoff',
  PickupAndDropoff: 'pickup_and_dropoff',
  OneOrMoreStops: 'one_or_more_stops',
} as const;

export type TaiLtlQuoteErrorStopRole =
  (typeof TAI_LTL_QUOTE_ERROR_STOP_ROLE)[keyof typeof TAI_LTL_QUOTE_ERROR_STOP_ROLE];

// Substrings emitted by drumkit Tai LTL location/mileage flows. FE maps these to user copy.
export const TAI_LTL_QUOTE_ERROR_MATCH_PATTERN = {
  NormalizeOrigin: 'normalize origin',
  NormalizeDestination: 'normalize destination',
  LookupOrigin: 'look up ltl origin location',
  LookupDestination: 'look up ltl destination location',
  InvalidZipCodes: 'zip codes are invalid',
} as const;

export const TAI_LTL_QUOTE_ERROR_USER_MESSAGE: Record<
  TaiLtlQuoteErrorStopRole,
  string
> = {
  [TAI_LTL_QUOTE_ERROR_STOP_ROLE.Pickup]:
    'The pickup address could not be validated. Please check the city, state, and ZIP code.',
  [TAI_LTL_QUOTE_ERROR_STOP_ROLE.Dropoff]:
    'The dropoff address could not be validated. Please check the city, state, and ZIP code.',
  [TAI_LTL_QUOTE_ERROR_STOP_ROLE.PickupAndDropoff]:
    'The pickup and dropoff addresses could not be validated. Please check the city, state, and ZIP code for each stop.',
  [TAI_LTL_QUOTE_ERROR_STOP_ROLE.OneOrMoreStops]:
    'One or more stop ZIP codes are invalid. Please check the pickup and dropoff addresses.',
};

export function classifyTaiLtlQuoteErrorStopRole(
  rawError: string
): Maybe<TaiLtlQuoteErrorStopRole> {
  const normalizedError = rawError.toLowerCase();

  if (
    normalizedError.includes(TAI_LTL_QUOTE_ERROR_MATCH_PATTERN.InvalidZipCodes)
  ) {
    return TAI_LTL_QUOTE_ERROR_STOP_ROLE.OneOrMoreStops;
  }

  if (
    normalizedError.includes(
      TAI_LTL_QUOTE_ERROR_MATCH_PATTERN.NormalizeOrigin
    ) ||
    normalizedError.includes(TAI_LTL_QUOTE_ERROR_MATCH_PATTERN.LookupOrigin)
  ) {
    return TAI_LTL_QUOTE_ERROR_STOP_ROLE.Pickup;
  }

  if (
    normalizedError.includes(
      TAI_LTL_QUOTE_ERROR_MATCH_PATTERN.NormalizeDestination
    ) ||
    normalizedError.includes(
      TAI_LTL_QUOTE_ERROR_MATCH_PATTERN.LookupDestination
    )
  ) {
    return TAI_LTL_QUOTE_ERROR_STOP_ROLE.Dropoff;
  }

  return null;
}

export function mapTaiLtlQuoteErrorsToUserMessage(
  quoteErrors: MaybeUndef<LTLQuickQuoteResponseQuoteError[]>
): Maybe<string> {
  if (!quoteErrors?.length) {
    return null;
  }

  let hasPickupIssue = false;
  let hasDropoffIssue = false;
  let hasUnknownStopIssue = false;

  for (const quoteError of quoteErrors) {
    const stopRole = classifyTaiLtlQuoteErrorStopRole(quoteError.error);

    if (stopRole === TAI_LTL_QUOTE_ERROR_STOP_ROLE.Pickup) {
      hasPickupIssue = true;
    } else if (stopRole === TAI_LTL_QUOTE_ERROR_STOP_ROLE.Dropoff) {
      hasDropoffIssue = true;
    } else if (stopRole === TAI_LTL_QUOTE_ERROR_STOP_ROLE.OneOrMoreStops) {
      hasUnknownStopIssue = true;
    }
  }

  if (hasPickupIssue && hasDropoffIssue) {
    return TAI_LTL_QUOTE_ERROR_USER_MESSAGE[
      TAI_LTL_QUOTE_ERROR_STOP_ROLE.PickupAndDropoff
    ];
  }

  if (hasPickupIssue) {
    return TAI_LTL_QUOTE_ERROR_USER_MESSAGE[
      TAI_LTL_QUOTE_ERROR_STOP_ROLE.Pickup
    ];
  }

  if (hasDropoffIssue) {
    return TAI_LTL_QUOTE_ERROR_USER_MESSAGE[
      TAI_LTL_QUOTE_ERROR_STOP_ROLE.Dropoff
    ];
  }

  if (hasUnknownStopIssue) {
    return TAI_LTL_QUOTE_ERROR_USER_MESSAGE[
      TAI_LTL_QUOTE_ERROR_STOP_ROLE.OneOrMoreStops
    ];
  }

  return null;
}
